import React, { useState, useEffect } from "react";
import "./basicinfo.scss";

// Hooks
import { useHistory, useLocation } from "react-router-dom";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers";

// Bootstrap
import Form from "react-bootstrap/Form";
import { Col, Row, Container, Modal, Button } from "react-bootstrap";

// Validation Rules
import SignupSchema from "./validations/registerValidation";
import {
    registerUser,
    registerPatient,
    connectInvitedPatientWithP,
} from "../../api/auth";
// import { sendVerificationCode } from '../../api/smsVerification'; // TEMPORARY: Disabled SMS verification
import {
    getProviderTokenInformation,
    getProviderID,
    sendConfirmEmail,
    checkExistingList,
} from "../../api/helpReq";
import {
    getDiagnoses,
    getProviderTypes,
    postDiagnoses,
} from "../../api/TBIRequests";

// Components
import Sallie from "../Sallie";
import PrivacyPagePatient from "../PrivacyPagePatient";
import PrivacyPage from "../PrivacyPage";
import LinkButton from "../StyledComponents/LinkButton";
import LabelledInput from "../LabelledInput";
import SubmitButton from "../StyledComponents/SubmitButton";
import OnboardingStepHeader from "./OnboardingStepHeader";
import PolicyComponent from "./PolicyComponent";

// lib
import useListSelector from "../../lib/useListSelector";

// API
import CountryList from "../../api/countryList.json";
import ClinicList from "../../api/clinicList.json";
import { verifyLicenseKey } from "../../api/paymentRequest";

// Translation
import { useTranslation } from "../../i18n/TranslationProvider";
import DiagnosisSelector from "../DiagnosisSelector";

const BasicInfo = ({
    setOnboardingPercent,
    setUser,
    setPatient,
    setPatientList,
}) => {
    const location = useLocation();
    const history = useHistory();
    const [isLoading, setIsLoading] = useState(false);
    const [viewPolicyPatient, setViewPolicyPatient] = useState(false);
    const [viewPolicyProvider, setViewPolicyProvider] = useState(false);
    const [alertPolicy, setAlertPolicy] = useState(true);
    const [messagePolicy, setMessagePolicy] = useState(true);
    const [providerEmail, setProviderEmail] = useState(null);
    const [providerTypes, setProviderTypes] = useState(null);
    const [providerId, setProviderId] = useState(null);
    const [diagnoses, setDiagnoses] = useState([]);
    const [selectedDiagnoses, setSelectedDiagnoses] = useState([]);
    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [errorMessage, setErrorMessage] = useState(false);
    const [registrationType, setRegistrationType] = useState("");
    const [agreed, setAgreed] = useState(false);
    const [readAlert, setReadAlert] = useState(false);
    const [selectedClinicName, setSelectedClinicName] = useState("");
    const [clinicLocations, setClinicLocations] = useState([
        "Manhattan, NY",
        "Rockville Centre, NY",
    ]);
    const [selectedCountry, setSelectedCountry] = useState("US");
    const [phoneCode, setPhoneCode] = useState("+1");
    const [countryFlag, setCountryFlag] = useState("🇺🇸");
    const [secondaryCountry, setSecondaryCountry] = useState("US");
    const [secondaryPhoneCode, setSecondaryPhoneCode] = useState("+1");
    const [secondaryCountryFlag, setSecondaryCountryFlag] = useState("🇺🇸");
    const [smsOptIn, setSmsOptIn] = useState(false);
    
    // State for provider eligibility error modal
    const [showProviderError, setShowProviderError] = useState(false);
    const [providerErrorMessage, setProviderErrorMessage] = useState("");
    const [showClinicConfirm, setShowClinicConfirm] = useState(false);
    const [pendingSubmitData, setPendingSubmitData] = useState(null);

    const url = new URLSearchParams(location.search);
    const token = url.get("token");
    const isProviderForm = token && !token.startsWith("invitedPatient");
    const isInvitedPatientByProvider =
        token && token.startsWith("invitedPatientP");
    const { register, handleSubmit, errors, setError, setValue } = useForm({
        resolver: yupResolver(SignupSchema({ isProviderForm })),
    });

    const { t, language } = useTranslation();

    useEffect(() => {
        setOnboardingPercent(10);

        getDiagnoses()
            .then((res) => {
                setDiagnoses(res.data);
            })
            .catch((err) => {
                console.error(err);
                history.push("/oops");
            });

        if (location.search.length > 0) {
            setValue("email", url.get("email"));
            setValue("firstName", url.get("first"));
            setValue("lastName", url.get("last"));
            if (isProviderForm) {
                const data = { token };
                getProviderTokenInformation({ data })
                    .then((res) => {
                        const { data } = res;
                        // setValue("email", data.providerEmail);
                        // Assuming we are hiding the email field for providers in the JSX
                        setProviderEmail(data.providerEmail);
                    })
                    .catch((err) => {
                        console.error(err);
                        history.push("/oops");
                    });
            } else if (isInvitedPatientByProvider) {
                const data = { token };
                getProviderID({ data })
                    .then((res) => {
                        const { data } = res;
                        setProviderId(data.provider_id);
                    })
                    .catch((err) => {
                        console.error(err);
                        history.push("/oops");
                    });
            }
        }
        
        // Initialize phone number fields with country code
        setValue("phoneNumber", phoneCode);
        setValue("secondaryPhoneNumber", secondaryPhoneCode);
    }, []);

    const handleRegistrationTypeChange = (event) => {
        const selectedType = event.target.value;
        setRegistrationType(selectedType);
        if (selectedType === "patient" || selectedType === "caregiver") {
            setClinicLocations(["Manhattan, NY", "Rockville Centre, NY", "Virtual"]);
        } else {
            setClinicLocations(["Manhattan, NY", "Rockville Centre, NY"]);
        }
    };
    const handleSelectDiagnosis = (id) => {
        if (selectedDiagnoses.includes(id)) {
            setSelectedDiagnoses(selectedDiagnoses.filter((d) => d != id));
        } else {
            setSelectedDiagnoses([...selectedDiagnoses, id]);
        }
    };
    const handleFirstNameChange = (event) => {
        setFirstName(event.target.value);
    };
    const handleLastNameChange = (event) => {
        setLastName(event.target.value);
    };

    const handleClinicNameChange = (event) => {
        const chosenClinicName = event.target.value;
        setSelectedClinicName(chosenClinicName);

        setValue("clinicName", chosenClinicName);

        const locations = ClinicList[chosenClinicName] || [];
        setClinicLocations(
            registrationType === "provider"
                ? locations.filter(loc => loc !== "Virtual")
                : locations
        );
    };

    const handleCountryChange = (event) => {
    const countryCode = event.target.value;
    setSelectedCountry(countryCode);
    
    // Convert country code to flag emoji
    const getFlagEmoji = (code) => {
        const codePoints = code
            .toUpperCase()
            .split('')
            .map(char => 127397 + char.charCodeAt());
        return String.fromCodePoint(...codePoints);
    };
    setCountryFlag(getFlagEmoji(countryCode));
    
    // Find the phone code for this country
    const countryEntry = Object.entries(CountryList).find(
        ([, value]) => (typeof value === 'object' ? value.code : value) === countryCode
    );
    
    if (countryEntry) {
        const countryData = countryEntry[1];
        const code = typeof countryData === 'object' ? countryData.phoneCode : '+1';
        setPhoneCode(code);
        setValue("phoneNumber", code);
    }};

    const handleSecondaryCountryChange = (event) => {
    const countryCode = event.target.value;
    setSecondaryCountry(countryCode);
    
    // Convert country code to flag emoji
    const getFlagEmoji = (code) => {
        const codePoints = code
            .toUpperCase()
            .split('')
            .map(char => 127397 + char.charCodeAt());
        return String.fromCodePoint(...codePoints);
    };
    setSecondaryCountryFlag(getFlagEmoji(countryCode));
    
    // Find the phone code for this country
    const countryEntry = Object.entries(CountryList).find(
        ([, value]) => (typeof value === 'object' ? value.code : value) === countryCode
    );
    
    if (countryEntry) {
        const countryData = countryEntry[1];
        const code = typeof countryData === 'object' ? countryData.phoneCode : '+1';
        setSecondaryPhoneCode(code);
        setValue("secondaryPhoneNumber", code);
    }};

    const [returnedData, setReturnedData] = useState([]);

    const { toggleFromSelectedList, addOther, selectedData, initialize } =
        useListSelector({
            setInitialData: setReturnedData,
            initialData: returnedData,
        });

    useEffect(() => {
        setOnboardingPercent(10);
        if (location.search.length > 0) {
            setValue("email", url.get("email"));
            setValue("firstName", url.get("first"));
            setValue("lastName", url.get("last"));
            if (isProviderForm) {
                const data = { token };
                getProviderTokenInformation({ data })
                    .then((res) => {
                        const { data } = res;
                        // setValue("email", data.providerEmail);
                        setProviderEmail(data.providerEmail);
                    })
                    .catch((err) => {
                        console.error(err);
                        history.push("/oops");
                    });
            }
        }
    }, []);

    useEffect(() => {
        basicInfoFormFill();
    }, []);

    const basicInfoFormFill = async () => {
        try {
            getProviderTypes().then((res) => {
                const { data } = res;
                setProviderTypes(data);
            });
        } catch {
            history.push("/oops");
        }
    };

    const onSubmit = async (data) => {
        if (!agreed) {
            setReadAlert(true);
            return;
        }
        setPendingSubmitData(data);
        setShowClinicConfirm(true);
    };

    const handleConfirmedSubmit = async () => {
        setShowClinicConfirm(false);
        const data = pendingSubmitData;

        if (data.dob && typeof data.dob === 'string' && data.dob.includes('-')) {
            const parts = data.dob.split('-');
            if (parts.length === 3 && parts[0].length === 4) {
                // It's in YYYY-MM-DD format, convert to MM-DD-YYYY
                data.dob = `${parts[1]}-${parts[2]}-${parts[0]}`;
            }
        }
        
        let patientType;
        let patientSubType;
        if (data.userType == "patient") {
            patientType = "Other";
            patientSubType = "Autism";
            data.patientType = patientType;
            data.patientSubType = patientSubType;
        }
        if (providerEmail) {
            data.email = providerEmail;
        }
        if (isProviderForm) {
            if (token.substring(0, 9) == "caregiver") {
                data.referral = "caregiver";
            } else if (token.substring(0, 7) == "patient") {
                data.referral = "patient";
            }
            data.userType = "provider";
        }

        try {
            setIsLoading(true);
            if (data.userType === "provider" || isProviderForm) {
                data.providerToken = token;
                data.messagePolicy = messagePolicy;
                data.alertPolicy = alertPolicy;
            }

            data.smsOptIn = smsOptIn;
            data.smsLanguage = smsOptIn ? 'en' : null;

            data.email = data.email.toLowerCase();

            // **NEW: Send SMS verification code BEFORE registering user**
            /*const fullPhoneNumber = data.phoneNumber;
            
            try {
                await sendVerificationCode(fullPhoneNumber);
            } catch (smsError) {
                console.error('SMS verification error:', smsError);
                setErrorMessage('Failed to send verification code. Please check your phone number and try again.');
                setIsLoading(false);
                return;
            }
            
            // Store all registration data in sessionStorage
            sessionStorage.setItem('registrationData', JSON.stringify(data));
            sessionStorage.setItem('registrationType', data.userType);
            sessionStorage.setItem('selectedDiagnoses', JSON.stringify(selectedDiagnoses));
            sessionStorage.setItem('patientType', patientType);
            sessionStorage.setItem('patientSubType', patientSubType);
            sessionStorage.setItem('messagePolicy', messagePolicy);
            sessionStorage.setItem('alertPolicy', alertPolicy);
            sessionStorage.setItem('isProviderForm', isProviderForm);
            sessionStorage.setItem('isInvitedPatientByProvider', isInvitedPatientByProvider);
            if (providerId) {
                sessionStorage.setItem('providerId', providerId);
            }
            if (token) {
                sessionStorage.setItem('token', token);
            }
            
            // Redirect to phone verification page
            history.push({
                pathname: '/Verification/PhoneVerify',
                state: { phoneNumber: fullPhoneNumber }
            });
            */
            // **TEMPORARY FIX: SMS verification disabled - register users directly**
            // TO DO: Re-enable when Twilio Verify Service is configured with new number
            
            const registerUserResponse = await registerUser(data);
            const responseUserData = registerUserResponse.data;
            setUser({ ...responseUserData, patientSubType, patientType });
            const { userId, userType } = responseUserData;

            // Send confirmation email (if not provider)
            if (userType !== "provider") {
                await sendConfirmEmail({
                    email: data.email,
                    name: data.firstName + " " + data.lastName,
                });
            }

            // Handle different user types
            if (userType === "caregiver") {
                history.push("/RegisterPatient", {
                    fromPatientControl: false,
                });
            } else if (userType === "patient" || (patientType === "Other" || patientType === "Dementia")) {
                // Register patient
                const patientData = {
                    firstName: data.firstName,
                    lastName: data.lastName,
                    dob: data.dob,
                    gender: data.gender || 'other',
                    relationshipType: 'self',
                    patientType: patientType,
                    patientSubType: patientSubType,
                };
                
                const patientResponse = await registerPatient(patientData);
                setPatient(patientResponse.data);
                
                // Add diagnoses if selected
                if (selectedDiagnoses && selectedDiagnoses.length > 0) {
                    await postDiagnoses({ 
                        data: selectedDiagnoses, 
                        patientId: patientResponse.data.patientId 
                    });
                }

                // Handle invited patient by provider
                if (isInvitedPatientByProvider && providerId) {
                    await connectInvitedPatientWithP({
                        patientId: patientResponse.data.patientId,
                        userId: userId,
                        providerId: providerId,
                    });
                }
                
                // Continue to next step
                history.push("/laterSymptoms");
            } else {
                // Provider
                // Verify license key for provider
                try {
                    const response = await verifyLicenseKey({
                        userId: userId,
                        licenseKey: "SALLIEFREEACCOUNT",
                    });
                    if (response.status === 200) {
                        console.log("setVerified is true");
                    } else {
                        console.error("oops error with provider license " + userId);
                    }
                } catch (error) {
                    console.error("Error verifying license key. " + userId);
                }
                
                // Take provider to patient control screen
                history.push("/patientControl");
            }
        } catch (err) {
            console.log(err);
            if (err.response && err.response.status === 402) {
                // Provider eligibility error - show modal/popup with backend message
                // DOUBLE-CHECK
                setProviderErrorMessage(err.response.data || t("basicInfo.providerError.fallback"));
                setShowProviderError(true);

            } else if (err.message && err.message.includes("409")) {
                setError("email", {
                    type: "emailTaken",
                    message: t("basicInfo.errors.emailTaken"),
                });
            } else if (err.message && err.message.includes("406")) {
                setError("email", {
                    type: "emailNotValid",
                    message: t("basicInfo.errors.emailNotValid"),
                });
            } else {
                history.push("/oops");
            }
            setIsLoading(false);
        }
    };

    /*const onSubmit = async (data) => {
        if (!agreed) {
            setReadAlert(true);
            return;
        }
        
        // Format date from YYYY-MM-DD to MM-DD-YYYY if it exists
        if (data.dob && typeof data.dob === 'string' && data.dob.includes('-')) {
            const parts = data.dob.split('-');
            if (parts.length === 3 && parts[0].length === 4) {
                // It's in YYYY-MM-DD format, convert to MM-DD-YYYY
                data.dob = `${parts[1]}-${parts[2]}-${parts[0]}`;
            }
        }
        
        let patientType;
        let patientSubType;
        if (data.userType == "patient") {
            patientType = "Other";
            patientSubType = "Autism";
            data.patientType = patientType;
            data.patientSubType = patientSubType;
        }
        if (providerEmail) {
            data.email = providerEmail;
        }
        if (isProviderForm) {
            if (token.substring(0, 9) == "caregiver") {
                data.referral = "caregiver";
            } else if (token.substring(0, 7) == "patient") {
                data.referral = "patient";
            }
            data.userType = "provider";
        }

        try {
            setIsLoading(true);
            if (data.userType === "provider" || isProviderForm) {
                data.providerToken = token;
                data.messagePolicy = messagePolicy;
                data.alertPolicy = alertPolicy;
            }

            // Add SMS opt-in data
            data.smsOptIn = smsOptIn;
            data.smsLanguage = smsOptIn ? 'en' : null;

            data.email = data.email.toLowerCase();

            const registerUserResponse = await registerUser(data);
            const responseUserData = registerUserResponse.data;
            setUser({ ...responseUserData, patientSubType, patientType });
            const { firstName, lastName, email, userType, userId } =
                responseUserData;

            if (userType !== "provider") {
                await sendConfirmEmail({
                    email: email,
                    name: firstName + " " + lastName,
                });
            }

            await checkExistingList({
                email: email,
            });

            if (userType === "caregiver") {
                history.push("/RegisterPatient", {
                    fromPatientControl: false,
                });
            } else if (
                (userType == "patient" && patientType == "Dementia") ||
                patientType == "Other"
            ) {
                // For those are not tbi patients
                data.relationshipType = "self";
                // data.status = combinedValues;
                const registerPatientResponse = await registerPatient(data);
                const responsePatientData = registerPatientResponse.data;
                setPatient(responsePatientData);
                postDiagnoses({
                    data: selectedDiagnoses,
                    patientId: responsePatientData.patientId,
                });
                if (isInvitedPatientByProvider) {
                    await connectInvitedPatientWithP({
                        patientId: responsePatientData.patientId,
                        userId: userId,
                        providerId: providerId,
                    });
                }

                history.push("/laterSymptoms", {
                    fromPatientControl: location.state?.fromPatientControl,
                });
            } else {
                //provider
                try {
                    const response = await verifyLicenseKey({
                        userId: userId,
                        licenseKey: "SALLIEFREEACCOUNT",
                    });
                    if (response.status === 200) {
                        console.log("setVerified is true");
                    } else {
                        console.error(
                            "oops error with provider license " + userId
                        );
                    }
                } catch (error) {
                    console.error("Error verifying license key. " + userId);
                }
                history.push("/patientControl"); // take the provider to patient and caregiver selector screen
            }
        } catch (err) {
            console.log(err);
            if (err.message && err.message.includes("409")) {
                setError("email", {
                    type: "emailTaken",
                    message: "Email is already taken!",
                });
            } else if (err.message && err.message.includes("406")) {
                setError("email", {
                    type: "emailNotValid",
                    message: "This is not valid email.",
                });
            } else {
                history.push("/oops");
            }
        } finally {
            setIsLoading(false);
        }
    };
    */

    let SallieMsg = t("basicInfo.sallieMsg");

    if (
        errors.dob &&
        errors.dob.message &&
        errors.dob.message === "You must be 13 or older"
    ) {
        SallieMsg = t("basicInfo.under13");
    }



    return (
        <>
            {/* Patient/Caregiver Terms Modal */}
            <div style={{ display: viewPolicyPatient ? "block" : "none" }}>
                <PrivacyPagePatient
                    firstName={firstName}
                    lastName={lastName}
                    registrationType={registrationType}
                    goback={() => {
                        setViewPolicyPatient(false);
                    }}
                    onClick={() => {
                        setAgreed(true);
                        setViewPolicyPatient(false);
                    }}
                />
            </div>

            {/* Provider Terms Modal */}
            <div style={{ display: viewPolicyProvider ? "block" : "none" }}>
                <PrivacyPage
                    firstName={firstName}
                    lastName={lastName}
                    goback={() => {
                        setViewPolicyProvider(false);
                    }}
                    onClick={() => {
                        setAgreed(true);
                        setViewPolicyProvider(false);
                    }}
                />
            </div>

            <div style={{ display: (viewPolicyPatient || viewPolicyProvider) ? "none" : "block" }}>
                <OnboardingStepHeader text={t("basicInfo.step1a")} />
                <Sallie text={SallieMsg} />
                <Form
                    autoComplete="on"
                    action="/post"
                    method="POST"
                    onSubmit={handleSubmit(onSubmit)}
                >
                    <Container>
                        <Row className="mt-3">
                            <Col md={12}>
                                <Form.Group controlId="userType">
                                    <Row>
                                        <Col>
                                            <Form.Label>
                                                {
                                                    t("basicInfo.registrationType.label")
                                                }
                                                <span className="required">
                                                    *
                                                </span>
                                            </Form.Label>
                                        </Col>
                                    </Row>
                                    <Row className="justify-content-center align-items-center">
                                        <Form.Check
                                            type="radio"
                                            label={
                                                t("basicInfo.registrationType.patient")
                                            }
                                            name="userType"
                                            value="patient"
                                            checked={
                                                registrationType === "patient"
                                            }
                                            onChange={
                                                handleRegistrationTypeChange
                                            }
                                            id="registrationTypePatient"
                                            className="registration-type-radio"
                                            ref={register}
                                            isInvalid={errors.userType}
                                        />
                                        <Form.Check
                                            type="radio"
                                            label={
                                                t("basicInfo.registrationType.caregiver")
                                            }
                                            name="userType"
                                            value="caregiver"
                                            checked={
                                                registrationType === "caregiver"
                                            }
                                            onChange={
                                                handleRegistrationTypeChange
                                            }
                                            id="registrationTypeCaregiver"
                                            className="registration-type-radio"
                                            ref={register}
                                            isInvalid={errors.userType}
                                        />
                                        <Form.Check
                                            type="radio"
                                            label={
                                                t("basicInfo.registrationType.provider")
                                            }
                                            name="userType"
                                            value="provider"
                                            checked={
                                                registrationType === "provider"
                                            }
                                            onChange={
                                                handleRegistrationTypeChange
                                            }
                                            id="registrationTypeProvider"
                                            className="registration-type-radio"
                                            ref={register}
                                            isInvalid={errors.userType}
                                        />
                                    </Row>
                                    <Row>
                                        <Col>
                                            {language === "en" &&
                                                errors.userType && (
                                                    <Form.Control.Feedback
                                                        type="invalid"
                                                        style={{
                                                            display: "block",
                                                        }}
                                                    >
                                                        {errors.userType &&
                                                            errors.userType
                                                                .message}
                                                    </Form.Control.Feedback>
                                                )}
                                        </Col>
                                    </Row>
                                </Form.Group>
                            </Col>
                        </Row>

                        {registrationType === "patient" && (
                            <Row>
                                <Col>
                                    {t("basicInfo.primaryDiagnosis")}
                                    <span className="required">*</span>
                                </Col>
                                <Col className="mt-3">
                                    <DiagnosisSelector
                                        items={diagnoses}
                                        selectedItems={selectedDiagnoses}
                                        selectItem={handleSelectDiagnosis}
                                    />
                                </Col>
                            </Row>
                        )}

                        {registrationType === "provider" && (
                            <Row>
                                <Col md={12}>
                                    <Form.Group controlId="providerType">
                                        <Form.Label>
                                            {t("basicInfo.providerType.label")}{" "}
                                            <span className="required">*</span>
                                        </Form.Label>
                                        <Form.Control
                                            autoFocus={true}
                                            name="providerType"
                                            as="select"
                                            ref={register}
                                            defaultValue=""
                                            isInvalid={errors.providerType}
                                        >
                                            <option value="" disabled>
                                                {
                                                    t("basicInfo.providerType.placeholder")
                                                }
                                            </option>
                                            {providerTypes && providerTypes.map(
                                                (providerType) => (
                                                    <option
                                                        key={
                                                            providerType.constantString
                                                        }
                                                        value={
                                                            providerType.constantString
                                                        }
                                                    >
                                                        {
                                                            t(`providerType.${providerType.constantString}`)
                                                        }
                                                    </option>
                                                )
                                            )}
                                        </Form.Control>

                                        {language === "en" &&
                                            errors.providerType && (
                                                <Form.Control.Feedback
                                                    type="invalid"
                                                    style={{ display: "block" }}
                                                >
                                                    {errors.providerType &&
                                                        errors.providerType
                                                            .message}
                                                </Form.Control.Feedback>
                                            )}
                                    </Form.Group>
                                </Col>
                            </Row>
                        )}

                        {registrationType && registrationType !== "provider" && (
                            <>
                                <Row noGutters>
                                    <Col md={3} className="pr-2">
                                        <LabelledInput
                                            name="firstName"
                                            required
                                            label={t("basicInfo.name.first")}
                                            autoComplete="given-name"
                                            onChange={handleFirstNameChange}
                                            inputRef={register}
                                            errors={errors}
                                        />
                                    </Col>

                                    <Col md={3} className="pr-2">
                                        <LabelledInput
                                            name="lastName"
                                            autoComplete="family-name"
                                            label={t("basicInfo.name.last")}
                                            onChange={handleLastNameChange}
                                            required
                                            inputRef={register}
                                            errors={errors}
                                        />
                                    </Col>

                                    {!isProviderForm && (
                                        <Col md={3} className="pr-2">
                                            <LabelledInput
                                                name="email"
                                                type="email"
                                                label={t("basicInfo.email")}
                                                autoComplete="username"
                                                required
                                                inputRef={register}
                                                errors={errors}
                                            />
                                        </Col>
                                    )}
                                    
                                    {!isProviderForm && (
                                        <Col md={3}>
                                            <Form.Group controlId="country">
                                                <Form.Label>
                                                    {t("basicInfo.country.label")}
                                                    <span className="required">*</span>
                                                </Form.Label>
                                                <Form.Control
                                                    name="country"
                                                    as="select"
                                                    ref={register}
                                                    value={selectedCountry}
                                                    onChange={handleCountryChange}
                                                    isInvalid={!!errors.country}
                                                >
                                                    {Object.keys(CountryList)
                                                        .sort()
                                                        .map((countryName) => {
                                                            const countryData = CountryList[countryName];
                                                            const countryCode = typeof countryData === 'object' ? countryData.code : countryData;
                                                            return (
                                                                <option
                                                                    key={countryCode}
                                                                    value={countryCode}
                                                                >
                                                                    {t(`country.${countryName}`)}
                                                                </option>
                                                            );
                                                        })}
                                                </Form.Control>
                                            </Form.Group>
                                        </Col>
                                    )}
                                </Row>

                                {!isProviderForm && (
                                    <Row noGutters>
                                        <Col md={6} className="pr-2">
                                            <Form.Group controlId="phoneNumber">
                                                <Form.Label>
                                                    {t("basicInfo.phone")}
                                                    <span className="required">*</span>
                                                </Form.Label>
                                                <div style={{ position: 'relative' }}>
                                                    <div
                                                        style={{
                                                            position: 'absolute',
                                                            left: '12px',
                                                            top: '50%',
                                                            transform: 'translateY(-50%)',
                                                            pointerEvents: 'none',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: '6px',
                                                            fontSize: '1rem',
                                                            zIndex: 1
                                                        }}
                                                    >
                                                        <span style={{ fontSize: '1.2rem' }}>{countryFlag}</span>
                                                        <span style={{ color: '#9ca3af' }}>{phoneCode}</span>
                                                        <span style={{ color: '#dee2e6' }}>|</span>
                                                    </div>
                                                    <Form.Control
                                                        name="phoneNumberDisplay"
                                                        type="tel"
                                                        isInvalid={!!errors.phoneNumber}
                                                        style={{
                                                            paddingLeft: `${phoneCode.length * 9 + 60}px`
                                                        }}
                                                        placeholder="2345678901"
                                                        onChange={(e) => {
                                                            const localNumber = e.target.value;
                                                            setValue('phoneNumber', phoneCode + localNumber);
                                                        }}
                                                    />
                                                    <input
                                                        type="hidden"
                                                        name="phoneNumber"
                                                        ref={register}
                                                    />
                                                    {errors.phoneNumber && (
                                                        <Form.Control.Feedback
                                                            type="invalid"
                                                            style={{ display: "block" }}
                                                        >
                                                            {errors.phoneNumber.message}
                                                        </Form.Control.Feedback>
                                                    )}
                                                </div>
                                            </Form.Group>
                                        </Col>
                                        <Col md={6}>
                                            <Form.Group controlId="dobPatient">
                                                <Form.Label>
                                                    {t("basicInfo.dob")}
                                                    <span className="required">*</span>
                                                </Form.Label>
                                                <Form.Control
                                                    type="date"
                                                    name="dob"
                                                    ref={register}
                                                    isInvalid={!!errors.dob}
                                                    placeholder="MM-DD-YYYY"
                                                    max={new Date().toISOString().split('T')[0]}
                                                />
                                                {errors.dob && (
                                                    <Form.Control.Feedback
                                                        type="invalid"
                                                        style={{ display: "block" }}
                                                    >
                                                        {errors.dob.message}
                                                    </Form.Control.Feedback>
                                                )}
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                )}

                                {!isProviderForm && (
                                    <Row noGutters>
                                        <Col md={6}>
                                            <Form.Group controlId="genderPatientCaregiver">
                                                <Form.Label>
                                                    {t("basicInfo.gender.label")}
                                                    <span className="required">*</span>
                                                </Form.Label>
                                                <Form.Control
                                                    name="gender"
                                                    as="select"
                                                    ref={register}
                                                    defaultValue=""
                                                    isInvalid={errors.gender}
                                                >
                                                    <option value="" disabled>{t("basicInfo.gender.placeholder")}</option>
                                                    <option value="male">{t("basicInfo.gender.male")}</option>
                                                    <option value="female">{t("basicInfo.gender.female")}</option>
                                                    <option value="other">{t("basicInfo.gender.other")}</option>
                                                </Form.Control>
                                                {errors.gender && (
                                                    <Form.Control.Feedback
                                                        type="invalid"
                                                        style={{ display: "block" }}
                                                    >
                                                        {errors.gender.message}
                                                    </Form.Control.Feedback>
                                                )}
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                )}

                                {!isProviderForm && (
                                    <Row noGutters className="mt-3">
                                        <Col md={12}>
                                            <Form.Group controlId="smsOptInPatient">
                                                <Form.Check
                                                    type="checkbox"
                                                    name="smsOptIn"
                                                    checked={smsOptIn}
                                                    onChange={(e) => setSmsOptIn(e.target.checked)}
                                                    label={
                                                        <span>
                                                            {t("basicInfo.sms.optIn")}
                                                            <span className="text-muted small d-block mt-1">
                                                                {t("basicInfo.sms.disclaimer")}
                                                            </span>
                                                        </span>
                                                    }
                                                />
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                )}

                                {!isProviderForm && registrationType === "caregiver" && (
                                    <Row noGutters>
                                        <Col md={3} className="pr-2">
                                            <Form.Group controlId="secondaryCountry">
                                                <Form.Label>
                                                    {t("basicInfo.alternateCountry")}
                                                </Form.Label>
                                                <Form.Control
                                                    name="secondaryCountry"
                                                    as="select"
                                                    ref={register}
                                                    value={secondaryCountry}
                                                    onChange={handleSecondaryCountryChange}
                                                >
                                                    {Object.keys(CountryList)
                                                        .sort()
                                                        .map((countryName) => {
                                                            const countryData = CountryList[countryName];
                                                            const countryCode = typeof countryData === 'object' ? countryData.code : countryData;
                                                            return (
                                                                <option
                                                                    key={countryCode}
                                                                    value={countryCode}
                                                                >
                                                                    {t(`country.${countryName}`)}
                                                                </option>
                                                            );
                                                        })}
                                                </Form.Control>
                                            </Form.Group>
                                        </Col>
                                        <Col md={3}>
                                            <Form.Group controlId="secondaryPhoneNumber">
                                                <Form.Label>
                                                    {t("basicInfo.alternatePhone")}
                                                </Form.Label>
                                                <div style={{ position: 'relative' }}>
                                                    <div
                                                        style={{
                                                            position: 'absolute',
                                                            left: '12px',
                                                            top: '50%',
                                                            transform: 'translateY(-50%)',
                                                            pointerEvents: 'none',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: '6px',
                                                            fontSize: '1rem',
                                                            zIndex: 1
                                                        }}
                                                    >
                                                        <span style={{ fontSize: '1.2rem' }}>{secondaryCountryFlag}</span>
                                                        <span style={{ color: '#9ca3af' }}>{secondaryPhoneCode}</span>
                                                        <span style={{ color: '#dee2e6' }}>|</span>
                                                    </div>
                                                    <Form.Control
                                                        name="secondaryPhoneNumberDisplay"
                                                        type="tel"
                                                        style={{
                                                            paddingLeft: `${secondaryPhoneCode.length * 9 + 60}px`
                                                        }}
                                                        placeholder="2345678901"
                                                        onChange={(e) => {
                                                            const localNumber = e.target.value;
                                                            if (localNumber) {
                                                                setValue('secondaryPhoneNumber', secondaryPhoneCode + localNumber);
                                                            } else {
                                                                setValue('secondaryPhoneNumber', '');
                                                            }
                                                        }}
                                                    />
                                                    <input
                                                        type="hidden"
                                                        name="secondaryPhoneNumber"
                                                        ref={register}
                                                    />
                                                </div>
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                )}

                                {!isProviderForm && (
                                    <Row noGutters>
                                        <Col md={6} className="pr-2">
                                            <LabelledInput
                                                name="postalCode"
                                                label={t("basicInfo.zipcode")}
                                                required
                                                inputRef={register}
                                                errors={errors}
                                                placeholder="12345"
                                            />
                                        </Col>

                                        <Col md={6}>
                                            <Form.Group controlId="visitType">
                                                <Form.Label>
                                                    {t("basicInfo.visitType.label")}
                                                    <span className="required">
                                                        *
                                                    </span>
                                                </Form.Label>
                                                <Form.Control
                                                    as="select"
                                                    name="visitType"
                                                    ref={register}
                                                    defaultValue=""
                                                    isInvalid={!!errors.visitType}
                                                >
                                                    <option value="" disabled>
                                                        {
                                                            t("basicInfo.visitType.placeholder")
                                                        }
                                                    </option>
                                                    <option value="in_person">
                                                        {
                                                            t("basicInfo.visitType.inPerson")
                                                        }
                                                    </option>
                                                    <option value="virtual">
                                                        {
                                                            t("basicInfo.visitType.virtual")
                                                        }
                                                    </option>
                                                    <option value="hybrid">
                                                        {
                                                            t("basicInfo.visitType.hybrid")
                                                        }
                                                    </option>
                                                </Form.Control>
                                                {language === "en" &&
                                                    errors.visitType && (
                                                        <Form.Control.Feedback
                                                            type="invalid"
                                                            style={{
                                                                display: "block",
                                                            }}
                                                        >
                                                            {
                                                                errors.visitType
                                                                    .message
                                                            }
                                                        </Form.Control.Feedback>
                                                    )}
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                )}
                                
                            </>
                        )}

                        {registrationType === "provider" && (
                            <>
                                <Row noGutters>
                                    <Col md={6} className="pr-2">
                                        <LabelledInput
                                            name="firstName"
                                            required
                                            label={t("basicInfo.name.first")}
                                            autoComplete="given-name"
                                            onChange={handleFirstNameChange}
                                            inputRef={register}
                                            errors={errors}
                                        />
                                    </Col>

                                    <Col md={6}>
                                        <LabelledInput
                                            name="lastName"
                                            autoComplete="family-name"
                                            label={t("basicInfo.name.last")}
                                            onChange={handleLastNameChange}
                                            required
                                            inputRef={register}
                                            errors={errors}
                                        />
                                    </Col>
                                </Row>
                                <Row noGutters>
                                    <Col md={3} className="pr-2">
                                        <LabelledInput
                                            name="email"
                                            type="email"
                                            label={t("basicInfo.email")}
                                            autoComplete="username"
                                            required
                                            inputRef={register}
                                            errors={errors}
                                        />
                                    </Col>
                                    <Col md={3} className="pr-2">
                                        <Form.Group controlId="countryProvider">
                                            <Form.Label>
                                                {t("basicInfo.country.label")}
                                                <span className="required">*</span>
                                            </Form.Label>
                                            <Form.Control
                                                name="country"
                                                as="select"
                                                ref={register}
                                                value={selectedCountry}
                                                onChange={handleCountryChange}
                                                isInvalid={!!errors.country}
                                            >
                                                {Object.keys(CountryList)
                                                    .sort()
                                                    .map((countryName) => {
                                                        const countryData = CountryList[countryName];
                                                        const countryCode = typeof countryData === 'object' ? countryData.code : countryData;
                                                        return (
                                                            <option
                                                                key={countryCode}
                                                                value={countryCode}
                                                            >
                                                                {t(`country.${countryName}`)}
                                                            </option>
                                                        );
                                                    })}
                                            </Form.Control>
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group controlId="dobProvider">
                                            <Form.Label>
                                                {t("basicInfo.dob")}
                                                <span className="required">*</span>
                                            </Form.Label>
                                            <Form.Control
                                                type="date"
                                                name="dob"
                                                ref={register}
                                                isInvalid={!!errors.dob}
                                                placeholder="MM-DD-YYYY"
                                                max={new Date().toISOString().split('T')[0]}
                                            />
                                            {errors.dob && (
                                                <Form.Control.Feedback
                                                    type="invalid"
                                                    style={{ display: "block" }}
                                                >
                                                    {errors.dob.message}
                                                </Form.Control.Feedback>
                                            )}
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <Row noGutters>
                                    <Col md={6}>
                                        <Form.Group controlId="genderProvider">
                                            <Form.Label>
                                                {t("basicInfo.gender.label")}
                                                <span className="required">*</span>
                                            </Form.Label>
                                            <Form.Control
                                                name="gender"
                                                as="select"
                                                ref={register}
                                                defaultValue=""
                                                isInvalid={errors.gender}
                                            >
                                                <option value="" disabled>{t("basicInfo.gender.placeholder")}</option>
                                                <option value="male">{t("basicInfo.gender.male")}</option>
                                                <option value="female">{t("basicInfo.gender.female")}</option>
                                                <option value="other">{t("basicInfo.gender.other")}</option>
                                            </Form.Control>
                                            {errors.gender && (
                                                <Form.Control.Feedback
                                                    type="invalid"
                                                    style={{ display: "block" }}
                                                >
                                                    {errors.gender.message}
                                                </Form.Control.Feedback>
                                            )}
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <Row noGutters>
                                    <Col md={6} className="pr-2">
                                        <LabelledInput
                                            name="postalCode"
                                            label={t("basicInfo.zipcode")}
                                            required
                                            inputRef={register}
                                            errors={errors}
                                            placeholder="12345"
                                        />
                                    </Col>
                                </Row>
                            </>
                        )}

                        <Row noGutters>
                            <Col md={6} className="pr-2">
                                <Form.Group controlId="clinicName">
                                    <Form.Label>
                                        {t("basicInfo.clinic.clinicName")}
                                        <span className="required">*</span>
                                    </Form.Label>
                                    <Form.Control
                                        name="clinicName"
                                        as="select"
                                        ref={register}
                                        value={selectedClinicName}
                                        onChange={handleClinicNameChange}
                                        isInvalid={!!errors.clinicName}
                                    >
                                        <option value="" disabled>
                                            {t("basicInfo.clinic.namePlaceholder")}
                                        </option>
                                        {Object.keys(ClinicList)
                                            .sort()
                                            .map((clinicName) => (
                                                <option
                                                    key={clinicName}
                                                    value={clinicName}
                                                >
                                                    {clinicName}
                                                </option>
                                            ))}
                                    </Form.Control>
                                    {errors.clinicName && (
                                        <Form.Control.Feedback
                                            type="invalid"
                                            style={{ display: "block" }}
                                        >
                                            {errors.clinicName.message}
                                        </Form.Control.Feedback>
                                    )}
                                </Form.Group>
                            </Col>

                            <Col md={6}>
                                <Form.Group controlId="clinicLocation">
                                    <Form.Label>
                                        {t("basicInfo.clinic.clinicLocation")}
                                        <span className="required">*</span>
                                    </Form.Label>
                                    <Form.Control
                                        name="clinicLocation"
                                        as="select"
                                        ref={register}
                                        defaultValue=""
                                        isInvalid={!!errors.clinicLocation}
                                    >
                                        <option value="" disabled>
                                            {t("basicInfo.clinic.locationPlaceholder")}
                                        </option>
                                        {clinicLocations.map(
                                            (location, index) => (
                                                <option
                                                    key={index}
                                                    value={location}
                                                >
                                                    {location}
                                                </option>
                                            )
                                        )}
                                    </Form.Control>
                                    {language === "en" &&
                                        errors.clinicLocation && (
                                            <Form.Control.Feedback
                                                type="invalid"
                                                style={{ display: "block" }}
                                            >
                                                {errors.clinicLocation.message}
                                            </Form.Control.Feedback>
                                        )}
                                </Form.Group>
                            </Col>
                        </Row>

                        
                        <Row noGutters>
                            <Col md={6} className="pr-2">
                                <LabelledInput
                                    name="password"
                                    type="password"
                                    label={t("basicInfo.password.password")}
                                    required
                                    autoComplete="new-password"
                                    inputRef={register}
                                    errors={errors}
                                />
                            </Col>
                            <Col md={6}>
                                <LabelledInput
                                    type="password"
                                    name="passwordConfirmation"
                                    label={t("basicInfo.password.confirmPassword")}
                                    autoComplete="new-password"
                                    required
                                    inputRef={register}
                                    errors={errors}
                                />
                            </Col>
                        </Row>

                        <Row>
                            <Col md={12} className="terms-and-conditions">
                                <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
                                    <input
                                        type="checkbox"
                                        checked={agreed}
                                        readOnly
                                        style={{ width: "18px", height: "18px", cursor: "default", flexShrink: 0 }}
                                    />
                                    <span>
                                        {t("basicInfo.terms.accept")}{" "}
                                    </span>
                                    <LinkButton
                                        type="button"
                                        onClick={() => {
                                            if (registrationType === "provider") {
                                                setViewPolicyProvider(true);
                                            } else {
                                                setViewPolicyPatient(true);
                                            }
                                        }}
                                    >
                                        <h5>
                                            {
                                                t("basicInfo.terms.termsAndConditions")
                                            }
                                        </h5>
                                    </LinkButton>
                                </div>
                                {readAlert && !agreed && (
                                    <Form.Control.Feedback
                                        style={{ display: "block" }}
                                        type="invalid"
                                    >
                                        {t("basicInfo.acceptTermsError")}
                                    </Form.Control.Feedback>
                                )}
                            </Col>
                        </Row>

                        <Row>
                            <Col md={12}>
                                <SubmitButton
                                    disabled={isLoading}
                                    type="submit"
                                >
                                    {t("onboarding.saveContinue")} &gt;
                                </SubmitButton>
                            </Col>
                        </Row>
                    </Container>
                </Form>
            </div>

            {/* Provider Eligibility Error Modal */}
            <Modal 
                show={showProviderError} 
                onHide={() => setShowProviderError(false)}
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title>{t("basicInfo.providerError.title")}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>{providerErrorMessage}</p>
                </Modal.Body>
                <Modal.Footer>
                    <Button 
                        variant="primary" 
                        onClick={() => setShowProviderError(false)}
                    >
                        {t("common.close")}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Clinic Confirmation Modal */}
            <Modal 
                show={showClinicConfirm}
                onHide={() => setShowClinicConfirm(false)}
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title>{t("basicInfo.clinicConfirm.title")}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>{t("basicInfo.clinicConfirm.body")}</p>
                    <div style={{ display: "flex", alignItems: "center", gap: "0.4rem", marginBottom: "0.5rem" }}>
                        <strong>{t("basicInfo.clinicConfirm.clinicName")}:</strong>
                        <span style={{ fontSize: "1.2rem", fontWeight: "bold" }}>{selectedClinicName}</span>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
                        <strong>{t("basicInfo.clinicConfirm.clinicLocation")}:</strong>
                        <span style={{ fontSize: "1.2rem", fontWeight: "bold" }}>{pendingSubmitData?.clinicLocation}</span>
                    </div>
                    <br />
                    <p>{t("basicInfo.clinicConfirm.question")}</p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowClinicConfirm(false)}>
                        {t("basicInfo.clinicConfirm.goBack")}
                    </Button>
                    <Button variant="primary" onClick={handleConfirmedSubmit}>
                        {t("basicInfo.clinicConfirm.continue")}
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default BasicInfo;